import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { IAuthResponse } from '@g99-product-web-ui/shared-interface-lib';
import {
  LocalStorageService,
  SubjectManageService,
  ToastService,
} from '@g99-product-web-ui/shared-service';
import {
  ExclamationIcon,
  MfaThumbnail,
} from '@g99-product-web-ui/shared-util-lib';

import { MultifactorAuthenticationService } from '../../services/multifactor-authentication.service';

@Component({
  selector: 'g99-mfa-verify',
  templateUrl: './mfa-verify.component.html',
  styleUrl: './mfa-verify.component.scss',
  standalone: false,
})
export class MfaVerifyComponent implements OnInit {
  protected mfaForm: FormGroup;
  protected isSubmitting = false;
  protected errorMessage: string | null = null;
  protected tempSession!: IAuthResponse;
  protected errorMessages = {
    code: {
      required: 'Verification code is required',
    },
  };
  protected exclamationSvg = ExclamationIcon;
  protected mfaThumbnail = MfaThumbnail;
  constructor(
    private readonly _fb: FormBuilder,
    private readonly _router: Router,
    private readonly _mfaSetupSevrice: MultifactorAuthenticationService,
    private _localStorageService: LocalStorageService,
    private readonly _subjectManagerService: SubjectManageService,
    private readonly _toastService: ToastService
  ) {
    this.mfaForm = this._fb.group({
      code: ['', Validators.required],
    });
  }

  /**
   * Lifecycle hook that initializes the component.
   * Retrieves the temporary session data from local storage to use during MFA verification.
   */
  ngOnInit(): void {
    this.tempSession = JSON.parse(
      localStorage.getItem('tempSessionInfo') || '{}'
    );
  }

  /**
   * Logs the user out by clearing local storage and redirects to the login page.
   */
  protected logoutAndLogin(): void {
    localStorage.clear();
    this._router.navigate(['/']);
  }

  /**
   * Handles the submission of the MFA verification form.
   * Constructs the form data and determines whether to use SMS or TOTP verification
   * based on the session challenge. Sends the form data for verification and processes
   * the response.
   */
  protected onSubmit(): void {
    const tenantId = this._localStorageService.readItem('tenantId') ?? null;
    const formData = {
      session: this.tempSession.session,
      username: this.tempSession.username,
      code: this.mfaForm.value.code,
      businessId: tenantId as number,
    };

    this.isSubmitting = true;

    const verificationObservable =
      this.tempSession.challengeName === 'SMS_MFA'
        ? this._mfaSetupSevrice.verifyLoginWithSms(formData)
        : this._mfaSetupSevrice.verifyLoginWithTotp(formData);

    verificationObservable.subscribe({
      next: (data) => this._handleSuccess(data),
      error: (error) => this._handleError(error.error),
    });
  }

  /**
   * Checks if a form control is invalid.
   * Used for showing error messages for specific fields in the form.
   *
   * @param controlName - Name of the form control to validate.
   * @returns `true` if the control is invalid and has been touched; otherwise, `false`.
   */
  protected isInvalid(controlName: string): boolean {
    const control = this.f[controlName];
    return !!control && control.touched && !!control.errors?.['required'];
  }

  /**
   * Handles a successful response from the MFA verification service.
   * Redirects the user to the appropriate page based on whether a password change is required,
   * and updates shared state with user information.
   * @param data - The authentication response containing session details and user information.
   */
  private _handleSuccess(data: IAuthResponse): void {
    this.isSubmitting = false;
    // Store the session in both cases — the change-password route is guarded
    // by AuthGuard, so without a stored session the user gets bounced straight
    // back to login instead of reaching the Change Password screen.
    this._localStorageService.storeItem('currentUser', data);
    this._subjectManagerService.userSubject.next(data);
    if (data.changePasswordForced) {
      this._router.navigate(['/users/change-password']);
    } else {
      this._router.navigate(['business/users']);
    }
  }

  /**
   * Handles errors during the MFA verification process.
   * Displays an error message and sets the `isSubmitting` flag to false.
   * @param error - The error object received from the verification service.
   */
  private _handleError(error: Error): void {
    this.isSubmitting = false;
    this.errorMessage = error.message;
    this._toastService.showError(this.errorMessage);
  }

  /**
   * Getter for form controls to facilitate easy access within the template.
   * @returns The controls of the MFA form.
   */
  protected get f() {
    return this.mfaForm.controls;
  }
}
